/**
 * Tela do laudo da modalidade assinatura eletrônica (E74.F3, ADR-0075 §7).
 *
 * A etapa "Laudo" do caso eletrônico. Não há narrativa a redigir nem critérios
 * a revisar: o documento sai pronto do que o motor apurou e do que o perito
 * concluiu em cada CCB na mesa. Esta tela existe para o perito **conferir o que
 * vai sair** antes de baixar — a mesma razão do preview do laudo grafotécnico.
 *
 * Montada pelo `LaudoCasoPage` quando `case.modalidade === 'eletronica'`; a
 * página recebe o caso já carregado e não o busca de novo.
 *
 * Quando o laudo ainda não pode ser emitido, a tela **nomeia o que falta**: o
 * servidor devolve as CCBs por concluir, e mandar o perito de volta à mesa com
 * a informação na mão é o que separa isto de um botão que não funciona.
 */
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { CaseStageStepper } from '@/components/Casos/CaseStageStepper';
import { LaudoPdfPreview } from '@/components/LaudoPdfPreview';
import { Blueprint, Button } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import { useApiClient } from '@/lib/api';
import type { CaseRecordData } from '@/lib/cases';
import { laudoDownloadBasename, triggerDocxDownload, triggerPdfDownload } from '@/lib/laudo';
import {
  getLaudoEletronicoDocx,
  getLaudoEletronicoPdf,
  getLaudoEletronicoPreview,
  LaudoEletronicoError,
} from '@/lib/laudo_eletronico';

const t = ptBR.laudoEletronico;

/** A recusa do gate, em linguagem acionável para o dono do caso. */
function textoDaRecusa(erro: LaudoEletronicoError): string {
  if (erro.reason !== 'not_ready') {
    return erro.reason === 'render_failed' ? t.errorRender : t.errorGeneric;
  }
  if (erro.motivo === 'exame_pendente' && erro.pendentes.length > 0) {
    return `${t.naoPronto} ${t.naoProntoPendentes.replace('{ccbs}', erro.pendentes.join(', '))}`;
  }
  if (erro.motivo === 'revisao_pendente') return `${t.naoPronto} ${t.naoProntoRevisao}`;
  if (erro.motivo === 'sem_ccb') return `${t.naoPronto} ${t.naoProntoSemCcb}`;
  return t.naoPronto;
}

function mensagem(err: unknown): string {
  if (err instanceof LaudoEletronicoError) {
    return err.reason === 'network' ? t.errorNetwork : textoDaRecusa(err);
  }
  return t.errorGeneric;
}

export interface LaudoEletronicoPageProps {
  caso: CaseRecordData;
}

export function LaudoEletronicoPage({ caso }: LaudoEletronicoPageProps): JSX.Element {
  const client = useApiClient();
  const navigate = useNavigate();
  const [baixando, setBaixando] = useState<'pdf' | 'docx' | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const baixar = useCallback(
    (formato: 'pdf' | 'docx') => {
      setBaixando(formato);
      setErro(null);
      void (async () => {
        try {
          const basename = laudoDownloadBasename(caso);
          if (formato === 'pdf') {
            triggerPdfDownload(await getLaudoEletronicoPdf(client, caso.id), basename);
          } else {
            triggerDocxDownload(await getLaudoEletronicoDocx(client, caso.id), basename);
          }
        } catch (err) {
          setErro(mensagem(err));
        } finally {
          setBaixando(null);
        }
      })();
    },
    [caso, client],
  );

  return (
    <section
      className="mx-auto max-w-5xl"
      data-testid="laudo-eletronico-page"
      data-modalidade="eletronica"
      aria-labelledby="laudo-eletronico-title"
    >
      <h1 id="laudo-eletronico-title" className="text-display-md text-fg">
        {t.pageTitle}
      </h1>
      <p className="mt-1 max-w-prose text-body-md text-fg-muted">{t.pageSubtitle}</p>

      <div className="mt-sc-4">
        <CaseStageStepper
          caseId={caso.id}
          workflowStatus={caso.workflow_status ?? null}
          activeStage="laudo"
          modalidade="eletronica"
        />
      </div>

      <div className="mt-sc-6 flex flex-wrap gap-sc-2">
        <Button
          type="button"
          onClick={() => baixar('pdf')}
          disabled={baixando !== null}
          data-testid="laudo-eletronico-pdf"
        >
          {baixando === 'pdf' ? t.baixando : t.baixarPdf}
        </Button>
        <Button
          type="button"
          variant="secondary"
          onClick={() => baixar('docx')}
          disabled={baixando !== null}
          data-testid="laudo-eletronico-docx"
        >
          {baixando === 'docx' ? t.baixando : t.baixarDocx}
        </Button>
        <Button
          type="button"
          variant="ghost"
          onClick={() => navigate(`/casos/${caso.id}/mesa-eletronica`)}
        >
          {t.irParaMesa}
        </Button>
      </div>

      {erro !== null ? (
        <p
          className="mt-sc-4 border border-danger bg-danger-50 px-sc-3 py-sc-2 text-body-sm text-danger-700"
          role="alert"
          data-testid="laudo-eletronico-error"
        >
          {erro}
        </p>
      ) : null}

      <Blueprint className="mt-sc-6 p-sc-4">
        <LaudoPdfPreview caseId={caso.id} fetchPdf={getLaudoEletronicoPreview} />
      </Blueprint>
    </section>
  );
}
